import { cookieName, type FeatureFlag, type FeatureFlagMap, featureFlagKeys } from '@coffee-to-go/common';
import Cookies from 'js-cookie';
import { create } from 'zustand';

interface FeatureFlagState {
  flags: FeatureFlagMap;
  setFlag: (key: FeatureFlag, value: boolean) => void;
  setMany: (flags: FeatureFlagMap) => void;
}

function setFeatureFlagsCookie(flags: FeatureFlagMap): void {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  const options: Cookies.CookieAttributes = { sameSite: 'lax' };

  // Only set domain for multi-level domains (not localhost, IPs, or single domains)
  if (parts.length > 2 && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    options.domain = parts.slice(-2).join('.');
  }

  Cookies.set(cookieName, JSON.stringify(flags), options);
}

export const useFeatureFlags = create<FeatureFlagState>()((set) => ({
  flags: {},
  setFlag: (key, value) =>
    set((s) => {
      const next = { ...s.flags, [key]: value };
      setFeatureFlagsCookie(next);
      return { flags: next };
    }),
  setMany: (map) =>
    set((s) => {
      const next = { ...s.flags, ...map };
      setFeatureFlagsCookie(next);
      return { flags: next };
    }),
}));

export const initFeatureFlags = (): void => {
  let parsed: FeatureFlagMap = {};

  // Query-string syntax:  ?ff.newDashboard=1&ff.betaCheckout=0
  const params = new URLSearchParams(window.location.search);
  featureFlagKeys.forEach((k) => {
    const v = params.get(`ff.${k}`);
    if (v !== null) parsed[k] = v === '1' || v === 'true';
  });

  // merge with cookie (if any)
  const cookieVal = Cookies.get(cookieName);
  if (cookieVal) {
    try {
      parsed = Object.assign({}, JSON.parse(cookieVal), parsed);
    } catch {
      // ignore malformed cookie
    }
  }

  // write into zustand → cookie will be updated automatically
  useFeatureFlags.getState().setMany(parsed);
};

export const useFeatureFlag = (key: FeatureFlag): boolean => useFeatureFlags((s) => !!s.flags[key]);
